// Per-Type Catalog , item-kind registry + helpers (Slice 1).
// docs/expansion-roadmap-2026/38-per-type-catalog-branch-sharing-and-inventory.md (Part 1)
//
// Single source of truth mapping each `menu_items.kind` to its editor label,
// pricing rules, and profile schema. Components read this registry and render
// generically , the same data-not-code discipline as lib/business-types/registry.ts.
// Client-safe (zod only); the menu-item editor imports it directly.


import { comboKind } from './combo';
import { dishKind } from './dish';
import { drinkKind } from './drink';
import { packageKind } from './package';
import { serviceKind } from './service';
import type { ItemKindDefinition } from './registry-types';
import {
  DEFAULT_ITEM_KIND,
  ITEM_KIND_IDS,
  type ItemKind,
  type ItemProfileByKind,
  type ItemTypeProfile,
  type PricingMode,
} from './types';

export * from './types';
export * from './relevance';
export * from './default-kind';
export * from './infer';
export type { ItemKindDefinition } from './registry-types';

// The registry. Keyed by kind; each value is fully self-describing.
export const ITEM_KINDS: {
  [K in ItemKind]: ItemKindDefinition<ItemProfileByKind[K]>;
} = {
  dish: dishKind,
  package: packageKind,
  drink: drinkKind,
  service: serviceKind,
  combo: comboKind,
};

/** All kind definitions in declaration order , for building a kind picker. */
export const ITEM_KIND_LIST: ItemKindDefinition<ItemTypeProfile>[] = ITEM_KIND_IDS.map(
  (id) => ITEM_KINDS[id] as ItemKindDefinition<ItemTypeProfile>,
);

export function isItemKind(value: unknown): value is ItemKind {
  return typeof value === 'string' && (ITEM_KIND_IDS as readonly string[]).includes(value);
}

/** Resolve a stored (possibly null/unknown) kind to a definition, safe-defaulting to `dish`. */
export function getItemKind(id: string | null | undefined): ItemKindDefinition<ItemTypeProfile> {
  return (isItemKind(id) ? ITEM_KINDS[id] : ITEM_KINDS[DEFAULT_ITEM_KIND]) as ItemKindDefinition<ItemTypeProfile>;
}

/** A fresh default profile for the given kind (falls back to dish's `{}`). */
export function defaultProfileForKind(id: string | null | undefined): ItemTypeProfile {
  return getItemKind(id).defaultProfile();
}

/** The default pricing mode for the given kind (falls back to `flat`). */
export function defaultPricingModeForKind(id: string | null | undefined): PricingMode {
  return getItemKind(id).defaultPricingMode;
}

/**
 * Validate + normalize a raw `type_profile` for a kind. Never throws: on invalid
 * input it falls back to the kind's default profile and reports the issue, so a
 * malformed legacy row can never break a read path. Returns `ok` for callers
 * (write paths) that DO want to reject bad input.
 */
export function parseTypeProfile(
  id: string | null | undefined,
  raw: unknown,
): { ok: boolean; profile: ItemTypeProfile; error?: string } {
  const def = getItemKind(id);
  const result = def.schema.safeParse(raw ?? def.defaultProfile());
  if (result.success) {
    return { ok: true, profile: result.data as ItemTypeProfile };
  }
  return {
    ok: false,
    profile: def.defaultProfile(),
    error: result.error.issues[0]?.message || 'Invalid item profile.',
  };
}
